import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check, CircleDollarSign, Lightbulb, PartyPopper, UserPlus, Wallet } from 'lucide-react';
import { Container, Glow, Section, SectionHeader } from './ui';
import { EASE, Reveal, Stagger, fadeUp } from './motion';

const STEPS = [
  {
    icon: UserPlus,
    title: 'Agree & Onboard',
    description: 'Set up your Groupmoney account and invite your crew—it’s fast and easy.',
    details: ['Create an account in under 2 minutes', 'Send email invites or share links'],
  },
  {
    icon: CircleDollarSign,
    title: 'Contribute',
    description: 'Everyone adds their share, with full visibility for all members.',
    details: ['Multiple payment methods available', 'Real-time contribution tracking'],
  },
  {
    icon: Lightbulb,
    title: 'Decide',
    description: 'Pick a goal or use AI suggestions to figure out what’s next for your group.',
    details: ['AI-powered idea suggestions', 'Collaborative decision tools'],
  },
  {
    icon: Wallet,
    title: 'Act',
    description: 'Spend the money with clear approvals and thorough documentation.',
    details: ['Customizable approval workflows', 'Receipt uploading and tracking'],
  },
  {
    icon: PartyPopper,
    title: 'Enjoy',
    description: 'Celebrate what you’ve accomplished together without the financial stress!',
    details: ['Achievement reporting and summaries', 'Share success stories with your group'],
  },
];

const HowItWorks = () => (
  <Section id="how-it-works" className="overflow-hidden">
    <Glow className="left-[-8rem] top-1/3 h-96 w-96 bg-brand-500/10" />
    <Container className="relative">
      <SectionHeader
        eyebrow="Simple Process"
        title="How Does It Work?"
        description="It's simple to get started with Groupmoney in just five steps."
      />

      <div className="relative mt-16 sm:mt-20">
        {/* Connector line drawn on scroll (desktop) */}
        <motion.div
          aria-hidden="true"
          className="absolute left-0 right-0 top-7 hidden h-px origin-left bg-gradient-to-r from-brand-400/0 via-brand-400/50 to-brand-400/0 lg:block"
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.4, ease: EASE }}
        />

        <Stagger as="ol" className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5 lg:gap-5" stagger={0.12}>
          {STEPS.map(({ icon: Icon, title, description, details }, i) => (
            <motion.li key={title} variants={fadeUp} className="relative flex flex-col">
              <div className="relative z-10 mb-6 flex items-center gap-3">
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl border border-white/10 bg-ink-900 text-brand-300 shadow-[0_0_30px_-8px_rgba(0,211,255,0.5)]">
                  <Icon className="h-6 w-6" />
                </span>
                <span className="text-sm font-semibold tabular-nums text-slate-500">0{i + 1}</span>
              </div>
              <div className="surface flex flex-1 flex-col p-6 transition-colors duration-300 hover:border-white/[0.16]">
                <h3 className="text-xl font-bold tracking-tight text-white">{title}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-400">{description}</p>
                <ul className="mt-5 space-y-2 border-t border-white/[0.06] pt-5">
                  {details.map((d) => (
                    <li key={d} className="flex items-start gap-2 text-sm text-slate-300">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-300" />
                      {d}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.li>
          ))}
        </Stagger>
      </div>

      <Reveal className="mt-20 text-center">
        <a href="#get-started" className="btn-primary group">
          Ready to Get Started?
          <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
        </a>
        <p className="mt-4 text-sm text-slate-500">No credit card required. Start your free trial today.</p>
      </Reveal>
    </Container>
  </Section>
);

export default HowItWorks;
